import axios from "axios";

// ── Global public demo-mode header injection ────────────────────────
// AuthContext + a handful of other older code paths use the top-level
// `axios` module directly (not the `api` instance below). To make sure
// the demo Bearer + X-Demo-Mode headers reach EVERY outbound request
// regardless of which axios flavor initiated it, we install a
// request interceptor on the default axios instance too. Reads the
// same `window.__trubeaute_demo_*` flags stamped by PublicDemoProvider.
axios.interceptors.request.use((config) => {
  if (typeof window !== "undefined" && window.__trubeaute_demo_mode) {
    config.headers = config.headers || {};
    config.headers["X-Demo-Mode"] = "1";
    if (window.__trubeaute_demo_session_id) {
      config.headers["X-Demo-Session"] = window.__trubeaute_demo_session_id;
    }
    if (
      window.__trubeaute_demo_session_token &&
      !config.headers.Authorization
    ) {
      config.headers.Authorization = `Bearer ${window.__trubeaute_demo_session_token}`;
    }
  }
  return config;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ── Public Demo Mode interceptor ────────────────────────────────────
// When the app is running on `demo.trubeaute.com` (or a preview URL
// with `REACT_APP_DEMO_MODE=true` / `?demo=1`), every outbound API
// call is tagged `X-Demo-Mode: 1`. The backend uses this header (in
// addition to Host + env-var checks) to route writes to demo-scoped
// collections. Reads from `window.__trubeaute_demo_mode` — a synchronous
// global flag stamped by `PublicDemoProvider` at mount time.
//
// Also injects `Authorization: Bearer <session_token>` if the demo
// session mint stashed a token on the window. This is a defensive
// fallback for browsers that don't immediately expose the httpOnly
// session_token cookie to XHR after Set-Cookie (headless Chrome under
// Playwright, and occasionally Safari).
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined" && window.__trubeaute_demo_mode) {
    config.headers = config.headers || {};
    config.headers["X-Demo-Mode"] = "1";
    if (window.__trubeaute_demo_session_id) {
      config.headers["X-Demo-Session"] = window.__trubeaute_demo_session_id;
    }
    if (
      window.__trubeaute_demo_session_token &&
      !config.headers.Authorization
    ) {
      config.headers.Authorization = `Bearer ${window.__trubeaute_demo_session_token}`;
    }
  }
  return config;
});

export const analyzeSelfie = async (payload) => {
  const { data } = await api.post("/analyze", payload, { timeout: 120000 });
  return data;
};

// Stateless counterpart used only by /demo. The API returns the routine in
// the response body and never creates a user, routine, token, or media row.
export const analyzeDemoSelfie = async (payload) => {
  const { data } = await api.post("/demo/analyze", payload, {
    timeout: 120000,
    // Make the privacy boundary explicit even if a signed-in visitor opens
    // /demo in the same browser: no account cookie accompanies the selfie.
    withCredentials: false,
  });
  return data;
};

export const fetchProducts = async (category) => {
  const { data } = await api.get("/products", {
    params: category ? { category } : {},
  });
  return data;
};

export const fetchRoutines = async () => {
  const { data } = await api.get("/routines");
  return data;
};

export const fetchRoutine = async (id) => {
  const { data } = await api.get(`/routines/${id}`);
  return data;
};

/**
 * Per-step "ModiFace-style" AR after-preview (Feb 2026).
 *
 * fetchStepPreview — cheap GET, used on step mount. Returns the
 *   cached preview if one was generated previously, OR throws a
 *   404 which the caller catches to show the "Show me what this
 *   does" CTA.
 *
 * generateStepPreview — heavier POST. Uploads the user's current-
 *   session selfie + step index. Nano Banana renders the "after"
 *   image, the backend caches it on the routine doc, and returns
 *   the same shape as fetchStepPreview. Idempotent: second call
 *   for the same step returns the cached image without a fresh
 *   Nano Banana spend.
 *
 * The selfie itself is NEVER stored backend-side. Only the
 * generated preview is persisted (and cascade-deleted if the user
 * deletes the routine).
 */
export const fetchStepPreview = async (routineId, stepIdx) => {
  const { data } = await api.get(
    `/routines/${routineId}/step-preview`,
    { params: { step_idx: stepIdx } },
  );
  return data;
};

export const generateStepPreview = async (routineId, payload) => {
  const { data } = await api.post(
    `/routines/${routineId}/step-preview`,
    payload,
    // Nano Banana can take 6-12s; bump above axios default
    { timeout: 60000 },
  );
  return data;
};

/**
 * Save-this-look "journey" share card (Feb 2026).
 *
 * Composes the user's cached per-step Nano Banana previews into one
 * 1080×1920 IG-Stories JPEG with the TruBeauté wordmark + tagline.
 * The composition itself is free (PIL stacks the previews already in
 * Mongo); the only cost is the one Nano Banana render per step that
 * the user already opted into.
 *
 * fetchShareCard — cheap GET, returns the cached composite if one
 *   exists, else 404. Used on modal open for instant display.
 *
 * generateShareCard — POST that triggers the compose. Returns 422
 *   with `missing_step_indices` when not every step has a preview yet
 *   — the modal then prompts "see step N first".
 */
export const fetchShareCard = async (routineId) => {
  const { data } = await api.get(`/routines/${routineId}/share-card`);
  return data;
};

export const generateShareCard = async (routineId) => {
  const { data } = await api.post(
    `/routines/${routineId}/share-card`,
    {},
    { timeout: 30000 },
  );
  return data;
};

/**
 * Fetch a single static technique tutorial by slug. Used to load the
 * AR-mirror experience when the user opens `/routine/technique:{slug}`
 * — the technique catalog lives outside `/routines` because there's
 * no DB row, so Routine.jsx falls back to this when it sees the
 * technique-prefixed id.
 */
export const fetchTechnique = async (slug) => {
  const { data } = await api.get(`/techniques/${slug}`);
  return data?.technique || null;
};

export const deleteRoutine = async (id) => {
  const { data } = await api.delete(`/routines/${id}`);
  return data;
};

export const saveSnapshot = async (payload) => {
  const { data } = await api.post("/snapshots", payload, { timeout: 60000 });
  return data;
};

export const fetchSnapshots = async (routineId, withImages = false) => {
  const params = {};
  if (routineId) params.routine_id = routineId;
  if (withImages) params.with_images = true;
  const { data } = await api.get("/snapshots", { params });
  return data;
};

export const fetchSnapshot = async (id) => {
  const { data } = await api.get(`/snapshots/${id}`);
  return data;
};

export const deleteSnapshot = async (id) => {
  const { data } = await api.delete(`/snapshots/${id}`);
  return data;
};

// ---- Billing / Pro subscription ----
export const fetchUsage = async () => {
  const { data } = await api.get("/me/usage");
  return data;
};

export const resendVerificationEmail = async () => {
  const { data } = await api.post("/auth/password/resend-verification");
  return data;
};

// First-routine activation nudge: idempotent dismiss endpoint.
export const dismissFirstRoutineNudge = async () => {
  const { data } = await api.post("/me/dismiss-first-routine-nudge");
  return data;
};

// ─── Inspiration ingestion (recreate-a-look flow) ──────────────
// POST a YouTube URL OR a free-text description; backend returns a
// structured `{brief, source}` we hand to /api/analyze later so the
// LLM adapts the look to the user's face/eye/skin profile.
export const ingestInspiration = async ({ url, description }) => {
  const { data } = await api.post("/inspiration/ingest", {
    url: url || null,
    description: description || null,
  });
  return data;
};

export const startCheckout = async (packageId = "pro_monthly") => {
  const { data } = await api.post("/billing/checkout", {
    package_id: packageId,
    origin_url: window.location.origin,
  });
  return data; // { url, session_id }
};

// ---- Privacy controls ----
export const exportMyData = async () => {
  const { data } = await api.get("/me/export");
  return data;
};

export const deleteMyAccount = async () => {
  const { data } = await api.delete("/me");
  return data;
};

export const fetchToneProfile = async () => {
  const { data } = await api.get("/profile/tone");
  return data;
};

export const saveToneProfile = async (payload) => {
  const { data } = await api.put("/profile/tone", payload);
  return data;
};

export const deleteToneProfile = async () => {
  const { data } = await api.delete("/profile/tone");
  return data;
};

export const updateLocale = async (locale) => {
  // Backend normalizes & validates against the supported set, so we
  // can pass any short code (en / fr / es / fr-CA) and let the server
  // decide. Returns the updated User payload.
  const { data } = await api.put("/profile/locale", { locale });
  return data;
};

// ─── Locked brands (power-user override) ───────────────────────────
export const fetchCatalogBrands = async () => {
  // Static catalog — distinct list of every brand we carry. The picker
  // uses this so users can only lock real brands. Cheap; no auth.
  const { data } = await api.get("/catalog/brands");
  return data?.brands || [];
};

export const fetchLockedBrands = async () => {
  const { data } = await api.get("/profile/locked-brands");
  return data || { brands: [], max_brands: 8 };
};

export const addLockedBrand = async (brand) => {
  const { data } = await api.post("/profile/locked-brands", { brand });
  return data;
};

export const removeLockedBrand = async (brand) => {
  // Path param is URL-encoded so multi-word brands like "Fenty Beauty"
  // round-trip cleanly through the router. Idempotent on the server —
  // missing brands return 200 with the current list.
  const { data } = await api.delete(
    `/profile/locked-brands/${encodeURIComponent(brand)}`,
  );
  return data;
};

export const checkBillingStatus = async (sessionId) => {
  const { data } = await api.get(`/billing/status/${sessionId}`);
  return data;
};


export const submitFeedback = async (payload) => {
  const { data } = await api.post("/feedback", payload);
  return data;
};

export const fetchAllFeedback = async () => {
  const { data } = await api.get("/admin/feedback");
  return data;
};

export const deleteFeedback = async (id) => {
  const { data } = await api.delete(`/admin/feedback/${id}`);
  return data;
};

export const fetchWaitlist = async () => {
  const { data } = await api.get("/admin/waitlist");
  return data;
};

export const waitlistCsvUrl = `${API}/admin/waitlist.csv`;

export const reactivateWaitlistSignup = async (id, payload) => {
  const { data } = await api.post(
    `/admin/waitlist/${id}/reactivate`,
    payload || {},
  );
  return data;
};

export const fetchAllergenCatalog = async () => {
  const { data } = await api.get("/allergens/catalog");
  return data;
};

export const fetchAllergenProfile = async () => {
  const { data } = await api.get("/profile/allergens");
  return data;
};

export const saveAllergenProfile = async (allergens) => {
  const { data } = await api.put("/profile/allergens", { allergens });
  return data;
};

export const fetchShadowZoneCatalog = async () => {
  const { data } = await api.get("/shadow-zones/catalog");
  return data;
};

export const fetchShadowZones = async () => {
  const { data } = await api.get("/profile/shadow-zones");
  return data;
};

export const saveShadowZones = async (zones) => {
  const { data } = await api.put("/profile/shadow-zones", { zones });
  return data;
};

export const fetchSkinProfileCatalog = async () => {
  const { data } = await api.get("/skin-profile/catalog");
  return data;
};

export const fetchSkinProfile = async () => {
  const { data } = await api.get("/profile/skin");
  return data;
};

export const saveSkinProfile = async (skinConcerns, lifeStage) => {
  const { data } = await api.put("/profile/skin", {
    skin_concerns: skinConcerns,
    life_stage: lifeStage,
  });
  return data;
};

export const fetchFaceShapeCatalog = async () => {
  const { data } = await api.get("/face-shape/catalog");
  return data;
};

export const fetchFaceShape = async () => {
  const { data } = await api.get("/profile/face-shape");
  return data;
};

export const saveFaceShape = async (shape, source = "detected") => {
  const { data } = await api.put("/profile/face-shape", { shape, source });
  return data;
};

export const deleteFaceShape = async () => {
  const { data } = await api.delete("/profile/face-shape");
  return data;
};

// ─── Eye shape ──────────────────────────────────────────────────────
// Per-user saved eye shape — hooded / monolid / almond / round /
// downturned / upturned / unsure. Drives the eyeshadow + liner
// technique copy in /api/analyze. Encrypted-at-rest server-side.
export const fetchEyeShape = async () => {
  const { data } = await api.get("/profile/eye-shape");
  return data;
};

export const saveEyeShape = async (shape, source = "user_set") => {
  const { data } = await api.put("/profile/eye-shape", { shape, source });
  return data;
};

export const deleteEyeShape = async () => {
  const { data } = await api.delete("/profile/eye-shape");
  return data;
};

export const fetchEyeShapeCatalog = async () => {
  const { data } = await api.get("/eye-shape/catalog");
  return data;
};

// ─── Founding Lock-In ─────────────────────────────────────────────
// First-100 Founding Lifetime members at $100 once. Two
// endpoints: a public counter for the landing page, and a combined
// register + Stripe-checkout call so the user lands both atomically.

export const foundingLockInStatus = async () => {
  const { data } = await api.get("/founding-lock-in/status");
  return data;
};

export const foundingLockInRegisterAndCheckout = async ({
  email,
  password,
  name,
  origin_url,
  locale,
  tos_version,
  campaign,
}) => {
  const { data } = await api.post("/founding-lock-in/register-and-checkout", {
    email,
    password,
    name,
    origin_url,
    locale: locale || undefined,
    tos_version: tos_version || undefined,
    campaign: campaign || undefined,
  });
  return data;
};

/**
 * Authenticated path into the same $100 Founding Lifetime offer.
 * Used when a Google-signed-in user clicks "Lock in Pride pricing"
 * on /pride — they already exist as a user, so we just stamp the
 * campaign and start checkout.
 */
export const claimCampaignAndCheckout = async ({ campaign, origin_url }) => {
  const { data } = await api.post(
    "/founding-lock-in/claim-and-checkout",
    { campaign, origin_url },
  );
  return data;
};

export const fetchStylePreferences = async () => {
  const { data } = await api.get("/profile/style-preferences");
  return data;
};

export const saveStylePreferences = async (preferences) => {
  const { data } = await api.put("/profile/style-preferences", preferences);
  return data;
};

export const deleteStylePreferences = async () => {
  const { data } = await api.delete("/profile/style-preferences");
  return data;
};

// ─── Beauty Arsenal ────────────────────────────────────────────────
// Structured list of products the user already owns, so the coach
// weaves them into routine tips instead of pushing new purchases.
export const fetchArsenalCatalog = async () => {
  const { data } = await api.get("/arsenal/catalog");
  return data;
};

export const fetchArsenal = async () => {
  const { data } = await api.get("/profile/arsenal");
  return data;
};

export const addArsenalItem = async (item) => {
  const { data } = await api.post("/profile/arsenal", item);
  return data;
};

export const updateArsenalItem = async (id, item) => {
  const { data } = await api.put(`/profile/arsenal/${id}`, item);
  return data;
};

export const deleteArsenalItem = async (id) => {
  const { data } = await api.delete(`/profile/arsenal/${id}`);
  return data;
};

// ─── Brand Affinity (user-facing algorithm override) ───────────────
// Lets users inspect + course-correct the brands the engine has
// learned they like. Soft control surface — not a hard block.
export const fetchBrandAffinity = async () => {
  const { data } = await api.get("/profile/brand-affinity");
  return data;
};

export const deleteBrandAffinity = async (brand) => {
  const { data } = await api.delete(
    `/profile/brand-affinity/${encodeURIComponent(brand)}`,
  );
  return data;
};

export const clearBrandAffinity = async () => {
  const { data } = await api.delete("/profile/brand-affinity");
  return data;
};

export const submitConnect = async (payload) => {
  const { data } = await api.post("/connect", payload);
  return data;
};


export const startGuestSession = async () => {
  const { data } = await api.post("/auth/guest", {});
  return data;
};

// ── Public Demo Mode session mint ───────────────────────────────────
// Called by `PublicDemoBootstrap` on first mount when the app is
// running in demo posture. Returns a `User` shaped like the guest
// endpoint — the demo user has `is_demo=true` and a 24h TTL server-side.
export const startPublicDemoSession = async () => {
  const { data } = await api.post("/demo/session", {});
  return data;
};

// ── Ambassador program ──────────────────────────────────────────────
// Tracking — public-safe
export const trackAmbassadorClick = async (handle) => {
  if (!handle) return { ok: false };
  try {
    const { data } = await api.post("/ambassador/click", { handle });
    return data;
  } catch {
    return { ok: false };
  }
};

// Called once after auth resolves to bind first-touch attribution.
export const attributeAmbassador = async (handle) => {
  if (!handle) return { ok: false };
  try {
    const { data } = await api.post("/ambassador/attribute", { handle });
    return data;
  } catch {
    return { ok: false };
  }
};

// Ambassador dashboard
export const fetchAmbassadorMe = async () => {
  const { data } = await api.get("/ambassador/me");
  return data;
};

export const claimAmbassadorInvite = async ({
  token,
  handle,
  payout_method,
  tax_note,
  display_name,
}) => {
  const { data } = await api.post("/ambassador/claim", {
    token,
    handle,
    payout_method,
    tax_note,
    display_name,
  });
  return data;
};

// One-step claim: public (the token is the credential, like a magic link).
export const getAmbassadorClaimInfo = async (token) => {
  const { data } = await api.get("/ambassador/claim/info", {
    params: { token },
  });
  return data;
};

export const completeAmbassadorClaim = async (payload) => {
  const { data } = await api.post("/ambassador/claim/complete", payload);
  return data;
};

// Pro gift invites — same token-as-credential pattern as ambassador claim.
export const getProClaimInfo = async (token) => {
  const { data } = await api.get("/pro/claim/info", { params: { token } });
  return data;
};

export const completeProClaim = async (payload) => {
  const { data } = await api.post("/pro/claim/complete", payload);
  return data;
};

export const adminCreateProInvite = async (body) => {
  const { data } = await api.post("/admin/pro-invites", body);
  return data;
};

export const adminListProInvites = async () => {
  const { data } = await api.get("/admin/pro-invites");
  return data;
};

// Admin
export const adminCreateAmbassadorInvite = async (body) => {
  const { data } = await api.post("/admin/ambassadors/invite", body);
  return data;
};

export const adminListAmbassadors = async () => {
  const { data } = await api.get("/admin/ambassadors");
  return data;
};

// Invited but not yet activated — drives the "Pending invites" panel.
export const adminListAmbassadorInvites = async () => {
  const { data } = await api.get("/admin/ambassadors/invites");
  return data;
};

export const adminMarkAmbassadorPayoutPaid = async ({
  handle,
  amount_cents,
  note,
}) => {
  const { data } = await api.post("/admin/ambassadors/payout", {
    handle,
    amount_cents,
    note,
  });
  return data;
};

// Public ambassador-program apply funnel + admin queue
export const submitAmbassadorApplication = async (body) => {
  const { data } = await api.post("/ambassador/apply", body);
  return data;
};

export const adminListAmbassadorApplications = async (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  const path = `/admin/ambassador-applications${qs.toString() ? `?${qs}` : ""}`;
  const { data } = await api.get(path);
  return data;
};

export const adminApproveAmbassadorApplication = async (
  applicationId,
  { note, commission_rate } = {},
) => {
  const { data } = await api.post(
    `/admin/ambassador-applications/${encodeURIComponent(applicationId)}/approve`,
    { note, commission_rate },
  );
  return data;
};

export const adminRejectAmbassadorApplication = async (
  applicationId,
  { note } = {},
) => {
  const { data } = await api.post(
    `/admin/ambassador-applications/${encodeURIComponent(applicationId)}/reject`,
    { note },
  );
  return data;
};


// ── Trouvé chat ────────────────────────────────────────────────────
// Gated to Pro · ambassador · ambassador-referred (with daily quota).
// 4xx errors surface as { detail: { error, reason, quota_left, ... }}
export const fetchTrouveAccess = async () => {
  const { data } = await api.get("/trouve/chat/access");
  return data;
};

export const createTrouveSession = async () => {
  const { data } = await api.post("/trouve/chat/sessions");
  return data;
};

export const listTrouveSessions = async (limit = 50) => {
  const { data } = await api.get("/trouve/chat/sessions", { params: { limit } });
  return data?.sessions || [];
};

export const fetchTrouveSession = async (sessionId) => {
  const { data } = await api.get(
    `/trouve/chat/sessions/${encodeURIComponent(sessionId)}`,
  );
  return data;
};

export const sendTrouveMessage = async (sessionId, { text, image_base64 } = {}) => {
  const { data } = await api.post(
    `/trouve/chat/sessions/${encodeURIComponent(sessionId)}/message`,
    { text, image_base64 },
    { timeout: 90_000 },
  );
  return data;
};

export const finalizeTrouveSession = async (sessionId) => {
  const { data } = await api.post(
    `/trouve/chat/sessions/${encodeURIComponent(sessionId)}/finalize`,
  );
  return data;
};

export const deleteTrouveSession = async (sessionId) => {
  const { data } = await api.delete(
    `/trouve/chat/sessions/${encodeURIComponent(sessionId)}`,
  );
  return data;
};

// Save a product Trouvé recommended in chat directly into the user's
// Beauty Arsenal. Idempotent server-side (same brand+name → returns
// the existing arsenal_item_id with already_saved=true).
export const saveTrouveRecommendation = async ({
  brand,
  name,
  category,
  messageId,
  sessionId,
}) => {
  const { data } = await api.post("/trouve/recommendations/save", {
    brand,
    name,
    category,
    message_id: messageId,
    session_id: sessionId,
  });
  return data;
};

export const transcribeTrouveVoice = async (audioBlob) => {
  // Browser MediaRecorder → webm/opus by default; we pass through.
  const fd = new FormData();
  const filename = audioBlob.name || "voice.webm";
  fd.append("audio", audioBlob, filename);
  const { data } = await api.post("/trouve/voice/transcribe", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60_000,
  });
  return data;
};

// Returns an object URL the caller is responsible for revoking after
// playback. Caps text at 1000 chars server-side. Cached on the backend
// so re-plays of the same message cost nothing.
export const speakTrouveText = async (text, { voice } = {}) => {
  const res = await api.post(
    "/trouve/voice/speak",
    { text, voice },
    { responseType: "blob", timeout: 30_000 },
  );
  const url = URL.createObjectURL(res.data);
  return url;
};

// Trigger a browser download of Trouvé's voice as an MP3. Reuses the
// `/trouve/voice/speak` endpoint (server cache makes a re-fetch ~free
// for text already played). The filename is sanitised + dated so the
// user's Downloads folder stays scannable.
const _slugify = (s) =>
  (s || "trouve")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "trouve";

export const downloadTrouveAudio = async (text, { voice, hint } = {}) => {
  const res = await api.post(
    "/trouve/voice/speak",
    { text, voice },
    { responseType: "blob", timeout: 30_000 },
  );
  const url = URL.createObjectURL(res.data);
  const date = new Date().toISOString().slice(0, 10);
  const slug = _slugify(hint || text);
  const filename = `trouve-${date}-${slug}.mp3`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Let the browser actually start the download before revoking.
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
  }, 1500);
  return filename;
};
